import { Box, Hidden, styled, Typography } from "@material-ui/core";
import { graphql } from "gatsby";
import React, { useEffect, useRef } from "react";
import { useInView } from "react-intersection-observer";
import { convertDatoCmsAssetUrlToCloudfrontUrl } from "../../helpers/cdn";
import { getAspectRatioPaddingTop } from "../card/utils";
import { BatteryPriceFragment as Props } from "./__generated__/BatteryPriceFragment";

export const CustomContainer = styled("div")(({ theme }) => ({
  position: "relative",
  width: "100%",
  paddingTop: getAspectRatioPaddingTop("2.23:1"),

  [theme.breakpoints.down("xs")]: {
    paddingTop: getAspectRatioPaddingTop("0.57:1"),
    "& .title-wrapper": {
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)"
    }
  },
  [theme.breakpoints.up("sm")]: {
    "& .title-wrapper": {
      top: "42%",
      left: "50%",
      transform: "translate(-50%, -42%)"
    }
  },
  [theme.breakpoints.up("md")]: {
    marginBottom: "-2px"
  }
}));

export const CustomVideo = styled("video")(() => ({
  width: "100%",
  height: "auto !important",
  outline: "0 !important",
  overflow: "hidden !important",
  position: "absolute",
  top: 0,
  left: 0
}));

export default function BatteryPriceSection({
  title,
  videoSources,
  videoSourcesMobile
}: Props) {
  const videoRef = useRef<HTMLVideoElement>();
  const [inViewRef, inView] = useInView({ threshold: 0.25 });

  /**
   * Video behaviour
   * 1. Play video when entering the viewport
   * 2. Reset video after leaving the viewport
   * 3. Play video again when re-entering the viewport
   */
  useEffect(() => {
    if (!videoRef.current) {
      return;
    }
    if (inView) {
      // Older browsers may not return a value from play()
      const promise = videoRef.current.play();
      if (promise) {
        promise.catch(e => {});
      }
    } else {
      try {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      } catch (error) {}
    }
  }, [inView]);

  return (
    <Box overflow="hidden" ref={inViewRef as any}>
      <CustomContainer>
        {/* Text layer on top of image */}
        <Box
          position="absolute"
          top={"50%"}
          left={"50%"}
          zIndex={200}
          className="title-wrapper"
        >
          <Hidden smUp implementation="css">
            <Typography
              variant="h3"
              className="white center"
              style={{ whiteSpace: "nowrap" }}
            >
              {title}
            </Typography>
          </Hidden>
          <Hidden xsDown implementation="css">
            <Typography
              variant="h4"
              className="white center"
              style={{ whiteSpace: "nowrap" }}
            >
              {title}
            </Typography>
          </Hidden>
        </Box>

        {/* Desktop video */}
        <Hidden xsDown>
          <CustomVideo
            controls={false}
            preload="auto"
            muted
            playsInline
            ref={videoRef}
            disableRemotePlayback={true}
          >
            {videoSources.map(videoSource => (
              <source
                key={videoSource.url}
                src={convertDatoCmsAssetUrlToCloudfrontUrl(videoSource.url)}
                type={videoSource.mimeType}
              />
            ))}
          </CustomVideo>
        </Hidden>

        {/* Mobile video */}
        <Hidden smUp>
          <CustomVideo
            controls={false}
            preload="auto"
            muted
            playsInline
            ref={videoRef}
            disableRemotePlayback={true}
          >
            {videoSourcesMobile.map(videoSource => (
              <source
                key={videoSource.url}
                src={convertDatoCmsAssetUrlToCloudfrontUrl(videoSource.url)}
                type={videoSource.mimeType}
              />
            ))}
          </CustomVideo>
        </Hidden>
      </CustomContainer>
    </Box>
  );
}

export const BatteryPriceFragment = graphql`
  fragment BatteryPriceFragment on DatoCmsCModuleBatteryPrice {
    title
    container
    containerDisableGutters
    containerBackgroundColor
    innerContainer
    innerContainerDisableGutters
    videoSources {
      mimeType
      url
    }
    videoSourcesMobile {
      mimeType
      url
    }
    model {
      apiKey
    }
  }
`;
